import { useI18n } from "i18n";
import { selectCurrentEvent } from "modules/event/selectors";
import { SpacesResponse, getSpacesData } from "modules/userForceMove/request";
import { selectCurrentTheaterId } from "modules/theater/selectors";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectRooms } from "store/rooms";
import { getOverflowSeats } from "helpers/roomHelper";
import { selectOccupiedAdminSeats } from "modules/room/redux/selectors";

interface Props {
  floorId: string;
  tableId: string;
}

export const useEventSpaces = ({ floorId, tableId }: Props) => {
  const [spacesData, setSpacesData] = useState<Record<string, SpacesResponse>>(
    {},
  );
  const eventData = useSelector(selectCurrentEvent);
  const theaterId = useSelector(selectCurrentTheaterId);
  const rooms = useSelector(selectRooms);
  const occupiedAdminSeats = useSelector(selectOccupiedAdminSeats);

  const { t } = useI18n(["manageEvent"]);

  const fetchData = useCallback(async (eventId: string, theaterId: string) => {
    const { spaces } = await getSpacesData(eventId, theaterId);

    setSpacesData(spaces);
  }, []);

  useEffect(() => {
    if (eventData?.id && theaterId) {
      fetchData(eventData.id, theaterId);
    }
  }, [eventData?.id, theaterId, fetchData]);

  const spaces = useMemo(() => Object.keys(spacesData), [spacesData]);

  const tables = useMemo(() => {
    if (!floorId) {
      return [];
    }

    const theaterRooms = Object.values(rooms || {});

    return spacesData[floorId]?.tables.map((table) => {
      const tableData = theaterRooms.find((room) => room.code === table.code);
      let overFlowSeats = 0;

      if (!eventData?.isOverflowSeatingDisabled) {
        overFlowSeats = getOverflowSeats(tableData?.positions.length || 0);
      }

      const occupiedSeats = occupiedAdminSeats.includes(table.id)
        ? table.occupiedSeats - 1
        : table.occupiedSeats;

      return {
        ...table,
        occupiedSeats,
        capacity: (tableData?.positions.length || 0) + overFlowSeats,
      };
    });
  }, [
    spacesData,
    floorId,
    rooms,
    eventData?.isOverflowSeatingDisabled,
    occupiedAdminSeats,
  ]);

  const selectedFloorName = t("manageEvent:floor.number", {
    number: spaces.indexOf(floorId) + 1,
  });

  const selectedTableName =
    tables?.find(({ id }) => id === tableId)?.name ?? "";

  const enableSelection = spaces.length > 0 && tables?.length > 0;

  return {
    spaces,
    tables,
    enableSelection,
    selectedFloorName,
    selectedTableName,
    spacesData,
  };
};
